import { Stack } from '@mui/material';
import { FileType } from '@procureai/constellation/foundation/file';
import { useFileStorage } from '@procureai/uix';
import { remove } from 'ramda';
import { useState } from 'react';

import { UploadAcceptProps, UploadDropZone } from '~/components/FileUpload/UploadDropZone/UploadDropZone';
import { UploadErrors } from '~/components/FileUpload/UploadErrorList/UploadErrorList';
import {
  ProgressIndicatorAlternating,
  ProgressIndicatorAlternatingProps,
} from '~/components/ProgressIndicator/ProgressIndicatorAlternating';
import { useBatchedFileUpload } from '~/hooks/useMultiFileUploadProgress/useMultiFileUploadProgress';

export interface DocumentUploadType {
  buttonLabel: string;
  onUploadSuccess: (data: { uploadId: string; fileName?: string }) => void;
  onUploadFailed: (error: Error) => void;
  uploadIndicatorProps: ProgressIndicatorAlternatingProps;
  accept?: UploadAcceptProps;
}

export const DocumentUpload = ({
  buttonLabel,
  onUploadSuccess,
  onUploadFailed,
  uploadIndicatorProps,
  accept,
}: DocumentUploadType) => {
  const [errors, setErrors] = useState<string[]>([]);
  const { upload: falconUpload } = useFileStorage();

  const upload = async (file: File): Promise<void> => {
    const { error, data } = await falconUpload({ data: { file, variant: FileType.ATTACHMENT } });
    if (error || data === null) {
      onUploadFailed(error);
      return;
    }
    onUploadSuccess({ uploadId: data.uploadId, fileName: file.name });
  };

  const { uploadBatch, isLoading: isUploading } = useBatchedFileUpload<void>(upload);

  return (
    <>
      {isUploading && <ProgressIndicatorAlternating {...uploadIndicatorProps} />}
      {!isUploading && (
        <Stack
          spacing={2}
          sx={{
            paddingTop: 2,
          }}
        >
          <UploadDropZone
            onDrop={uploadBatch}
            disabled={isUploading}
            accept={accept ?? { 'application/pdf': ['.pdf'] }}
            buttonLabel={buttonLabel}
            onDropErrors={(errorMessages) => setErrors(errorMessages)}
          />
          <UploadErrors errors={errors} onDiscard={(index) => setErrors(remove(index, 1))} />
        </Stack>
      )}
    </>
  );
};
